// 🚀 CONFIGURAÇÕES DE PERFORMANCE OTIMIZADAS PARA MOBILE

export const PERFORMANCE_CONFIG = {
  // 🔧 OTIMIZAÇÃO: TIMEOUTS MAIS RÁPIDOS PARA RESPONSIVIDADE
  TIMEOUTS: {
    CRITICAL: 3000,     // 3s para operações críticas (auth, primeira música) - reduzido
    STANDARD: 4000,     // 4s para operações normais (buscar músicas) - reduzido
    BACKGROUND: 6000,   // 6s para operações em background - reduzido
    NETWORK: 8000,      // 8s máximo para rede lenta - reduzido
  },

  // 🎵 AUDIO ULTRA OTIMIZADO
  AUDIO: {
    PRELOAD_COUNT: 0,           // 🚀 DESABILITADO: Sem preload de áudio
    BUFFER_SIZE: 'none',        // 🚀 OTIMIZADO: Sem buffer inicial
    MAX_RETRY_ATTEMPTS: 1,      // Máximo 1 tentativa para ser mais rápido
    RETRY_DELAY: 200,           // 200ms entre tentativas (mais rápido)
    FADE_DURATION: 50,          // Fade ultra rápido de 50ms
  },

  // 🖼️ IMAGENS OTIMIZADAS PARA JORNADA
  IMAGES: {
    PRELOAD_COUNT: 6,           // 🚀 OTIMIZADO: Preload das primeiras 6 capas
    BATCH_SIZE: 4,              // 4 imagens por batch (balanceado)
    LAZY_THRESHOLD: 100,        // 100px antes de carregar (mais responsivo)
    CACHE_TTL: 30 * 60 * 1000,  // 30 minutos de cache (equilibrado)
    LOAD_TIMEOUT: 3000,         // 3s timeout por imagem (mais tolerante)
    BATCH_TIMEOUT: 5000,        // 5s timeout por batch (mais tolerante)
    BACKGROUND_DELAY: 2000,     // 2s delay para carregamento em background (mais rápido)
    OFFLINE_LIMIT: 5 * 1024 * 1024, // 5MB limite para cache offline (mais generoso)
  },

  // 💾 CACHE MÍNIMO
  CACHE: {
    MAX_ENTRIES: 50,            // Máximo 50 entradas (muito reduzido)
    TTL_SHORT: 1 * 60 * 1000,   // 1 minuto para dados dinâmicos (mais curto)
    TTL_MEDIUM: 5 * 60 * 1000,  // 5 minutos para músicas (mais curto)
    TTL_LONG: 2 * 60 * 60 * 1000, // 2h para dados estáticos (mais curto)
    CLEANUP_INTERVAL: 2 * 60 * 1000, // Limpeza a cada 2min (muito mais frequente)
  },

  // 📊 LOADING ULTRA OTIMIZADO
  LOADING: {
    SPLASH_MIN: 200,            // Mínimo 200ms de splash (mais rápido)
    SPLASH_MAX: 1000,           // Máximo 1s de splash (mais rápido)
    SKELETON_DELAY: 50,         // Skeleton após 50ms (mais rápido)
    RETRY_COUNT: 0,             // 🚀 DESABILITADO: Sem retry (mais rápido)
  },

  // 🔄 REQUISIÇÕES MÍNIMAS
  REQUESTS: {
    BATCH_SIZE: 20,             // 20 músicas por batch (menor)
    MAX_CONCURRENT: 1,          // Máximo 1 requisição simultânea (evitar sobrecarga)
    DEBOUNCE_DELAY: 100,        // 100ms de debounce (mais rápido)
    THROTTLE_DELAY: 25,         // 25ms de throttle (mais rápido)
  },

  // 📱 MOBILE ULTRA OTIMIZADO
  MOBILE: {
    TOUCH_DELAY: 10,            // 10ms de delay para touch (mais rápido)
    SCROLL_THROTTLE: 16,        // 60fps de scroll (mantém)
    GESTURE_TIMEOUT: 100,       // 100ms para gestos (mais rápido)
    VIBRATION_DURATION: 0,      // 🚀 DESABILITADO: Sem vibração
  }
};

// 🎯 CONFIGURAÇÕES POR AMBIENTE
export const getOptimizedTimeout = (operation: keyof typeof PERFORMANCE_CONFIG.TIMEOUTS): number => {
  const baseTimeout = PERFORMANCE_CONFIG.TIMEOUTS[operation];
  
  // 📱 Para mobile, reduzir ainda mais
  const isMobile = /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  if (isMobile) {
    return Math.min(baseTimeout * 0.5, baseTimeout); // 50% mais rápido no mobile
  }
  
  return baseTimeout;
};

// 🔍 DETECÇÃO DE PERFORMANCE DO DISPOSITIVO
export const getDevicePerformance = (): 'low' | 'medium' | 'high' => {
  // Detecta baseado em hardware
  const memory = (navigator as any).deviceMemory || 4;
  const cores = navigator.hardwareConcurrency || 4;
  
  if (memory >= 8 && cores >= 8) return 'high';
  if (memory >= 4 && cores >= 4) return 'medium';
  return 'low';
};

// ⚡ TIMEOUTS ADAPTATIVOS BASEADOS NO DISPOSITIVO
export const getAdaptiveTimeout = (operation: keyof typeof PERFORMANCE_CONFIG.TIMEOUTS): number => {
  const baseTimeout = getOptimizedTimeout(operation);
  const performance = getDevicePerformance();
  
  switch (performance) {
    case 'low':
      return baseTimeout * 1.0;  // Mesmo tempo para dispositivos lentos
    case 'medium':
      return baseTimeout * 0.8;  // 20% mais rápido para dispositivos médios
    case 'high':
      return baseTimeout * 0.6;  // 40% mais rápido para dispositivos potentes
    default:
      return baseTimeout;
  }
}; 